import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";

const SKILLS = [
  "React", "TypeScript", "Next.js", "Node.js",
  "Python", "PostgreSQL", "Docker", "AWS",
  "GraphQL", "TailwindCSS", "Figma", "Git",
  "Redis", "Linux", "CI/CD", "REST API",
];

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

export function About({ personalInfo, language = "fr" }: { personalInfo?: any, language?: string }) {
  const { ref, visible } = useInView(0.1);
  const [hovered, setHovered] = useState<string | null>(null);

  const words = language === "en"
    ? ["I", "build", "products", "that", "make", "sense."]
    : ["Je", "construis", "des", "produits", "qui", "ont", "du", "sens."];
  const highlighted = language === "en"
    ? ["build", "products", "sense."]
    : ["construis", "produits", "sens."];

  return (
    <section
      id="about"
      ref={ref}
      className="relative py-24 md:py-36 overflow-hidden"
      style={{ background: "#050508" }}
    >
      {/* Background accent */}
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none"
        style={{
          width: "600px",
          height: "600px",
          background: "radial-gradient(circle, rgba(167,139,250,0.04) 0%, transparent 70%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section tag */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={visible ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-16"
        >
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "11px",
              color: "#22d3ee",
              letterSpacing: "0.15em",
            }}
          >
            {language === "en" ? "01 // ABOUT" : "01 // À PROPOS"}
          </span>
          <div className="flex-1 h-px" style={{ background: "rgba(34,211,238,0.15)" }} />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Left - Big statement */}
          <div>
            <div
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "clamp(36px, 5vw, 64px)",
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
              }}
            >
              {words.map((word, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  animate={visible ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="inline-block mr-3"
                  style={{
                    color: highlighted.includes(word) ? "#22d3ee" : "#ffffff",
                  }}
                >
                  {word}
                </motion.span>
              ))}
            </div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={visible ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="mt-8"
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "16px",
                color: "rgba(255,255,255,0.5)",
                lineHeight: 1.8,
                maxWidth: "480px",
              }}
            >
              {language === "en" ? "Passionate developer with over " : "Développeur passionné avec plus de "}
              <span style={{ color: "rgba(255,255,255,0.85)" }}>
                {personalInfo?.years_of_experience ? `${personalInfo.years_of_experience} ${language === "en" ? "years" : "ans"}` : (language === "en" ? "5 years" : "5 ans")} {language === "en" ? "of experience" : "d'expérience"}
              </span>{" "}
              {language === "en"
                ? "in building web and mobile applications. I enjoy solving complex problems with elegant solutions, always putting the user at the center."
                : "dans la création d'applications web et mobiles. J'aime résoudre des problèmes complexes avec des solutions élégantes, en mettant toujours l'utilisateur au centre."}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={visible ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.95, duration: 0.6 }}
              className="mt-4"
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "16px",
                color: "rgba(255,255,255,0.5)",
                lineHeight: 1.8,
                maxWidth: "480px",
              }}
            >
              {language === "en" ? "Based in " : "Basé à "}
              <span style={{ color: "#a78bfa" }}>{personalInfo?.region || "Paris, France 🇫🇷"}</span>. {language === "en" ? "Available for remote or hybrid opportunities." : "Disponible pour des opportunités en remote ou hybride."}
            </motion.p>

            {/* Code block */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={visible ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 1.1, duration: 0.6 }}
              className="mt-8 p-5 rounded-2xl"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "13px",
              }}
            >
              <div style={{ color: "rgba(255,255,255,0.25)", marginBottom: "8px" }}>// profil.ts</div>
              <div>
                <span style={{ color: "#a78bfa" }}>const</span>
                <span style={{ color: "#22d3ee" }}> sami</span>
                <span style={{ color: "rgba(255,255,255,0.5)" }}> = </span>
                <span style={{ color: "#f97316" }}>{"{"}</span>
              </div>
              <div className="pl-5">
                <div>
                  <span style={{ color: "rgba(255,255,255,0.5)" }}>{language === "en" ? "name: " : "nom: "}</span>
                  <span style={{ color: "#86efac" }}>"{personalInfo?.name || "Sami Cakiral"}"</span><span style={{ color: "rgba(255,255,255,0.3)" }}>,</span>
                </div>
                <div>
                  <span style={{ color: "rgba(255,255,255,0.5)" }}>role: </span>
                  <span style={{ color: "#86efac" }}>"{personalInfo?.title || "Full-Stack Dev"}"</span><span style={{ color: "rgba(255,255,255,0.3)" }}>,</span>
                </div>
                <div>
                  <span style={{ color: "rgba(255,255,255,0.5)" }}>disponible: </span>
                  <span style={{ color: "#22d3ee" }}>true</span><span style={{ color: "rgba(255,255,255,0.3)" }}>,</span>
                </div>
                <div>
                  <span style={{ color: "rgba(255,255,255,0.5)" }}>café: </span>
                  <span style={{ color: "#f97316" }}>Infinity</span>
                </div>
              </div>
              <div style={{ color: "#f97316" }}>{"}"}</div>
            </motion.div>
          </div>

          {/* Right - Skills */}
          <div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={visible ? { opacity: 1 } : {}}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="mb-6"
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "11px",
                color: "rgba(255,255,255,0.3)",
                letterSpacing: "0.12em",
              }}
            >
              STACK TECHNIQUE
            </motion.div>

            <div className="flex flex-wrap gap-3">
              {SKILLS.map((skill, i) => (
                <motion.button
                  key={skill}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={visible ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.6 + i * 0.04, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  onMouseEnter={() => setHovered(skill)}
                  onMouseLeave={() => setHovered(null)}
                  className="px-4 py-2 rounded-full transition-all"
                  style={{
                    background:
                      hovered === skill
                        ? "rgba(34,211,238,0.12)"
                        : "rgba(255,255,255,0.04)",
                    border:
                      hovered === skill
                        ? "1px solid rgba(34,211,238,0.4)"
                        : "1px solid rgba(255,255,255,0.07)",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "13px",
                    color: hovered === skill ? "#22d3ee" : "rgba(255,255,255,0.6)",
                    transition: "all 0.2s",
                    cursor: "none",
                  }}
                >
                  {skill}
                </motion.button>
              ))}
            </div>

            {/* Qualities */}
            <div className="mt-12 space-y-4">
              {[
                { label: "Clean Code", pct: 92 },
                { label: "UX Focus", pct: 88 },
                { label: "Performance", pct: 85 },
              ].map(({ label, pct }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, x: 20 }}
                  animate={visible ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 1.0 + i * 0.12, duration: 0.5 }}
                >
                  <div className="flex justify-between mb-1.5">
                    <span
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "13px",
                        color: "rgba(255,255,255,0.6)",
                      }}
                    >
                      {label}
                    </span>
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: "12px",
                        color: "#22d3ee",
                      }}
                    >
                      {pct}%
                    </span>
                  </div>
                  <div
                    className="h-1 rounded-full overflow-hidden"
                    style={{ background: "rgba(255,255,255,0.05)" }}
                  >
                    <motion.div
                      initial={{ width: 0 }}
                      animate={visible ? { width: `${pct}%` } : {}}
                      transition={{ delay: 1.2 + i * 0.12, duration: 1, ease: "easeOut" }}
                      className="h-full rounded-full"
                      style={{
                        background: "linear-gradient(90deg, #22d3ee, #a78bfa)",
                      }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
